import { format, parseISO } from 'date-fns';
import {
  CompanyDailyNavConfigVersion,
  DailyNavConfigApplyDate,
  DailyNavConfigApplyPreview,
  DailyNavRecord,
  DailyNavRecordConfig,
  FirmDailyNavConfigUpdate,
  FirmDailyNavConfigVersion,
} from 'api';

// The API stores thresholds as a fraction of 1 (0.10 = 10%, bounded to
// [0, 1]) with up to 4 decimal places. The UI shows/accepts a percentage
// with up to 2 decimal places so the round trip through /100 and *100 never
// loses precision or exceeds the API's 4 decimal places.
export const THRESHOLD_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
export const THRESHOLD_MAX_PERCENT = 100;

export const isValidThresholdPercent = (value: string): boolean => {
  const trimmed = value.trim();
  return THRESHOLD_PATTERN.test(trimmed) && Number(trimmed) <= THRESHOLD_MAX_PERCENT;
};

export const percentToFraction = (percent: string): string => (Number(percent.trim()) / 100).toFixed(4);

export const fractionToPercent = (fraction?: string | null): string =>
  fraction === null || fraction === undefined || fraction === ''
    ? ''
    : Number((Number(fraction) * 100).toFixed(4)).toString();

export type DailyNavThresholdKey =
  | 'cap_iq_short_window_delta'
  | 'cap_iq_long_window_delta'
  | 'cap_iq_since_last_valuation_delta'
  | 'secondary_short_window_delta'
  | 'secondary_long_window_delta'
  | 'secondary_since_last_valuation_delta';

export type DailyNavThresholdField = {
  key: DailyNavThresholdKey;
  label: string;
};

export type DailyNavThresholdGroup = {
  title: string;
  fields: DailyNavThresholdField[];
};

// Labels name the window a threshold is judged over ("2 Trading Sessions Δ").
// Callers pass the counts that apply to what they render — typed values in the
// live form, the saved counts in history — or nothing for the abstract wording.
const windowThresholdLabel = (
  sessions: number | string | null | undefined,
  fallback: string
): string => {
  const count = Number(sessions);
  if (!Number.isInteger(count) || count < 1) {
    return fallback;
  }
  return `${count} Trading Session${count === 1 ? '' : 's'} Δ`;
};

export const getDailyNavThresholdGroups = (
  shortWindowSessions?: number | string | null,
  longWindowSessions?: number | string | null
): DailyNavThresholdGroup[] => {
  const shortLabel = windowThresholdLabel(shortWindowSessions, 'Short Window Δ');
  const longLabel = windowThresholdLabel(longWindowSessions, 'Long Window Δ');
  return [
    {
      title: 'Capital IQ',
      fields: [
        { key: 'cap_iq_short_window_delta', label: shortLabel },
        { key: 'cap_iq_long_window_delta', label: longLabel },
        { key: 'cap_iq_since_last_valuation_delta', label: 'Since Last Valuation Δ' },
      ],
    },
    {
      title: 'Secondary Transaction',
      fields: [
        { key: 'secondary_short_window_delta', label: shortLabel },
        { key: 'secondary_long_window_delta', label: longLabel },
        { key: 'secondary_since_last_valuation_delta', label: 'Since Last Valuation Δ' },
      ],
    },
  ];
};

// Abstract-label form: supplies the keys/ordering.
export const DAILY_NAV_THRESHOLD_GROUPS: DailyNavThresholdGroup[] = getDailyNavThresholdGroups();

export const DAILY_NAV_THRESHOLD_KEYS: DailyNavThresholdKey[] = DAILY_NAV_THRESHOLD_GROUPS.flatMap(group =>
  group.fields.map(field => field.key)
);

export const DAILY_NAV_FIRM_DEFAULTS_COPY =
  'The defaults can be superseded at a company level. If no threshold is set at the company level, these defaults will be used.';

// Firm-only. Kept out of DAILY_NAV_THRESHOLD_GROUPS/_KEYS because those are
// shared with the company override form, which must never render these.
export type DailyNavWindowSessionsKey = 'short_window_sessions' | 'long_window_sessions';

export type DailyNavWindowSessionsField = {
  key: DailyNavWindowSessionsKey;
  label: string;
};

export const DAILY_NAV_WINDOW_SESSIONS_FIELDS: DailyNavWindowSessionsField[] = [
  { key: 'short_window_sessions', label: 'Short Window (trading sessions)' },
  { key: 'long_window_sessions', label: 'Long Window (trading sessions)' },
];

export const DAILY_NAV_WINDOW_SESSIONS_KEYS: DailyNavWindowSessionsKey[] = DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(
  field => field.key
);

export const DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS = 1;
export const DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS = 5;

export const WINDOW_SESSIONS_MIN = 1;
export const WINDOW_SESSIONS_MAX = 31;
// Whole trading sessions only; up to 2 digits covers the [1, 31] range.
export const WINDOW_SESSIONS_PATTERN = /^\d{1,2}$/;

export const isValidWindowSessions = (value: string): boolean => {
  const trimmed = value.trim();
  if (!WINDOW_SESSIONS_PATTERN.test(trimmed)) {
    return false;
  }
  const numeric = Number(trimmed);
  return numeric >= WINDOW_SESSIONS_MIN && numeric <= WINDOW_SESSIONS_MAX;
};

// Ordering only; assumes both already passed isValidWindowSessions.
export const isShortWindowBeforeLongWindow = (shortValue: string, longValue: string): boolean =>
  Number(shortValue.trim()) < Number(longValue.trim());

// The NAV day comes from a trading market, not a stored closing time — its
// calendar supplies the session close. Supported markets are NOT listed here;
// they come from the generated enum, so the picker cannot offer one the API
// would reject. Only the labels below are a frontend concern.
export type DailyNavTradingMarket = FirmDailyNavConfigUpdate.trading_calendar;
// Local alias for member access; re-exporting a const of the same name trips
// @typescript-eslint/no-redeclare and nothing else needs the members.
const TradingMarket = FirmDailyNavConfigUpdate.trading_calendar;

// A Record, not a list, so adding or removing a market backend-side is a
// compile error rather than a blank dropdown row.
const TRADING_MARKET_LABELS: Record<DailyNavTradingMarket, string> = {
  [TradingMarket.XNYS]: 'New York Stock Exchange (XNYS)',
  [TradingMarket.XNAS]: 'Nasdaq (XNAS)',
  [TradingMarket.XTSE]: 'Toronto Stock Exchange (XTSE)',
  [TradingMarket.XLON]: 'London Stock Exchange (XLON)',
  [TradingMarket.XETR]: 'Deutsche Börse Xetra (XETR)',
  [TradingMarket.XPAR]: 'Euronext Paris (XPAR)',
  [TradingMarket.XTKS]: 'Tokyo Stock Exchange (XTKS)',
  [TradingMarket.XHKG]: 'Hong Kong Stock Exchange (XHKG)',
  [TradingMarket.XASX]: 'Australian Securities Exchange (XASX)',
};

export const DAILY_NAV_TRADING_MARKET_OPTIONS: { value: DailyNavTradingMarket; label: string }[] =
  Object.values(TradingMarket).map(value => ({
    value,
    label: TRADING_MARKET_LABELS[value],
  }));

// Falls back to the raw code so an unsupported value (only possible from data
// predating the restriction) renders as itself rather than blank.
export const tradingMarketLabel = (value?: string | null): string =>
  (value && TRADING_MARKET_LABELS[value as DailyNavTradingMarket]) || value || '—';

// Seeds the form for a firm with no saved template; also what existing config
// versions were backfilled to.
export const DAILY_NAV_DEFAULT_TRADING_MARKET: DailyNavTradingMarket =
  TradingMarket.XNYS;

// Shared between the firm-wide (DailyNavSettings) and company-scoped (DailyMonitoring) Apply buttons.
export const DAILY_NAV_APPLY_BUTTON_LABEL = 'Apply to Open NAV Day';

export const DAILY_NAV_APPLY_STANDING_EXPLANATION =
  'Config changes apply to the next NAV day automatically. Saving also offers to apply them to the day currently in progress, and you can apply later from the Apply action on the open version in Version History; a day that has already closed can never be changed.';

export const DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING =
  'This also changes that day’s trading market and/or delta windows, so every delta on the date will be recomputed.';

// Shown when `dates` comes back empty (no NAV day at all), distinct from blocked_reason
// (a day exists but can't be applied) -- usually because today's records aren't generated yet.
export const DAILY_NAV_APPLY_NO_OPEN_DAY_HINT =
  'No NAV day is currently in progress to apply to. This is expected if today’s records have not been generated yet.';

export const DAILY_NAV_APPLY_UNSAVED_CHANGES_HINT =
  'Save your changes first. Applying uses the saved open version, not the unsaved edits on this page.';

// Distinct from "no open NAV day": a day IS open, but every record already sits on the
// open config, so applying would move nothing.
export const DAILY_NAV_APPLY_NOTHING_TO_APPLY_HINT =
  'The open NAV day is already using these settings — there is nothing to apply.';

// Splits the preview's `dates` into what the Apply button can act on and what it should
// explain. `dates` is NOT "the open NAV day": it covers every recent NAV day, closed ones
// included (they carry a blocked_reason instead of being dropped), so `dates[0]` is
// yesterday's closed day for the whole of today. Apply writes to every applicable date,
// so callers get the full applicable subset rather than one entry.
export const selectDailyNavApplyDates = (dates?: DailyNavConfigApplyDate[] | null) => {
  const allDates = dates ?? [];
  // Newest first, so `blockedDate` is the day the operator is actually asking about.
  // Sorted here rather than trusting the API's order, which the button's behavior
  // would otherwise silently depend on. nav_date is a sortable ISO date-only string.
  const newestFirst = [...allDates].sort((left, right) => (right.nav_date ?? '').localeCompare(left.nav_date ?? ''));
  const applicableDates = newestFirst.filter(date => !date.blocked_reason);

  return {
    hasDates: allDates.length > 0,
    applicableDates,
    recordCount: applicableDates.reduce((total, date) => total + (date.record_count ?? 0), 0),
    marketOrWindowsChange: applicableDates.some(date => date.market_changes || date.windows_change),
    // The most recent date only when nothing is applicable: that is the one whose
    // blocked_reason explains the disabled button.
    blockedDate: applicableDates.length ? null : (newestFirst[0] ?? null),
  };
};

// Renders the applicable date(s) for the confirm dialog. Normally one day; the API can
// return two when a call straddles a session close, and Apply would write to both.
export const dailyNavApplyDatesLabel = (applicableDates: DailyNavConfigApplyDate[]): string =>
  applicableDates
    // parseISO, not new Date: nav_date is date-only, which new Date reads as UTC
    // midnight -- a day early for any viewer west of UTC.
    .map(date => (date.nav_date ? format(parseISO(date.nav_date), 'MMM d, yyyy') : 'this date'))
    .join(' and ');

// Appends the viewer's local time to a closed-day message; the API only states the close
// in the market's zone, which the viewer would otherwise have to convert themselves.
export const dailyNavApplyBlockedMessage = (
  blockedReason?: string | null,
  // Nullable, not just optional: a date with no anchor reports session_close as null
  // while still carrying a blocked_reason.
  sessionCloseIso?: string | null
): string => {
  if (!blockedReason) {
    return '';
  }
  const closedAt = sessionCloseIso ? new Date(sessionCloseIso) : null;
  // Only a CLOSED day gets the local-time suffix -- the other refusals aren't about the
  // close instant. Computed client-side against session_close; wording only, so skew is harmless.
  const hasClosePassed = closedAt !== null && !Number.isNaN(closedAt.getTime()) && closedAt <= new Date();
  if (!hasClosePassed) {
    return blockedReason;
  }
  // Intl, not date-fns: date-fns has no dependency-free named-zone token (yields "GMT-7");
  // Intl gives the viewer's own "PDT" natively.
  const localClose = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(closedAt);
  return `${blockedReason} That was ${localClose} your time.`;
};

// Why an apply that returned 200 still moved nothing, or '' if it was simply already
// current. The server re-decides eligibility at apply time and can refuse after the
// preview said yes -- most often because the day's market closed between page load and
// click. Its reason has to outrank the idempotent "nothing needed applying" message,
// which would otherwise tell the operator their change landed when it did not.
export const dailyNavApplyRefusalMessage = (dates?: DailyNavConfigApplyDate[] | null): string => {
  // The newest blocked date, not the first: the service returns closed earlier days
  // too, and the operator is asking about the most recent one.
  const blocked = selectDailyNavApplyDates(dates).blockedDate;
  if (blocked) {
    return dailyNavApplyBlockedMessage(blocked.blocked_reason, blocked.session_close);
  }
  // blockedDate is null when something WAS applicable, so 0 moved means it was
  // already current -- '' lets the caller say so. No rows at all means the firm has
  // no NAV day on record to act on.
  return (dates ?? []).length ? '' : DAILY_NAV_APPLY_NO_OPEN_DAY_HINT;
};

// Which version governs the day in progress vs. the next NAV day. Read from the
// APPLICABLE dates only: a closed day is not the one being decided about, and letting
// it answer would label the wrong version as "in effect".
export const selectDailyNavGoverningVersions = (preview?: DailyNavConfigApplyPreview | null) => {
  const { applicableDates } = selectDailyNavApplyDates(preview?.dates);
  // Newest applicable day; selectDailyNavApplyDates already sorted newest-first.
  const currentDate = applicableDates[0] ?? null;
  const anchoredVersionId = currentDate?.current_firm_config_version_id ?? null;
  const openVersionId = currentDate?.open_firm_config_version_id ?? null;
  return {
    anchoredVersionId,
    openVersionId,
    // A DIFFERENT id space from firm versions: a company version history must match
    // against this, never anchoredVersionId. Company-scoped previews only.
    anchoredCompanyVersionId: currentDate?.current_company_config_version_id ?? null,
    navDate: currentDate?.nav_date ?? null,
    nextNavDate: preview?.next_nav_date ?? null,
    // Both known, or neither: a null id means "no open NAV day", not "aligned".
    hasDivergence:
      anchoredVersionId !== null && openVersionId !== null && anchoredVersionId !== openVersionId,
  };
};

// parseISO, not new Date: date-only strings read as UTC midnight, a day early west
// of UTC.
export const dailyNavDateLabel = (isoDate?: string | null): string =>
  isoDate ? format(parseISO(isoDate), 'MMM d, yyyy') : '';

// For a version's effective_from, a TIMESTAMP. Config versions change mid-day, so two
// sharing a date is common and the date alone cannot say which is running.
export const dailyNavDateTimeLabel = (isoDateTime?: string | null): string =>
  isoDateTime ? format(parseISO(isoDateTime), 'MMM d, yyyy h:mm a') : '';

// The date only when the server could name it -- never computed client-side, which
// cannot see weekends or holidays.
export const dailyNavNextDayLabel = (nextNavDate?: string | null): string =>
  nextNavDate ? `Takes effect next NAV day (${dailyNavDateLabel(nextNavDate)})` : 'Takes effect next NAV day';

// The rules the grid's rows were produced under, read off the first anchored row --
// the firm half is identical across a date's records.
//
// `isStale` needs isOpenNavDay as well as is_latest_firm_config: that flag is false on
// EVERY historical date by design, so alone it would warn about correct data.
export const selectDailyNavDayConfig = (
  records?: DailyNavRecord[] | null,
  isOpenNavDay?: boolean
) => {
  const config = (records ?? []).find(record => record.config)?.config ?? null;
  return {
    config,
    isStale: Boolean(config && config.is_latest_firm_config === false && isOpenNavDay),
    // Rows the grid can't explain at all: no anchor, so no rules to name.
    unanchoredCount: (records ?? []).filter(record => !record.config).length,
  };
};

// Presence in company_overrides is the test, not value inequality: an override equal
// to today's firm default is still an override, and will not follow it when it moves.
export const dailyNavRowHasOverrides = (config?: DailyNavRecordConfig | null): boolean =>
  Boolean(config && Object.keys(config.company_overrides ?? {}).length > 0);

// One changed setting. `from`/`to` are display strings, so a consumer never has to
// know which field it is looking at.
export type DailyNavConfigDiffRow = {
  key: string;
  label: string;
  from: string;
  to: string;
};

const NONE_LABEL = '—';
const INHERITED_LABEL = 'Firm default';

const thresholdDisplay = (fraction?: string | null): string => {
  const percent = fractionToPercent(fraction);
  return percent === '' ? NONE_LABEL : `${percent}%`;
};

const recipientsDisplay = (recipients: unknown): string =>
  Array.isArray(recipients) && recipients.length ? (recipients as string[]).join(', ') : 'None';

// Differing fields only between two saved firm versions; empty means identical.
// Threshold labels come from the SUBSEQUENT version's window counts -- a row describes
// the state being moved to.
export const buildDailyNavFirmConfigDiff = (
  before?: FirmDailyNavConfigVersion | null,
  after?: FirmDailyNavConfigVersion | null
): DailyNavConfigDiffRow[] => {
  if (!before || !after) {
    return [];
  }
  const rows: DailyNavConfigDiffRow[] = [];

  const thresholdLabels = new Map(
    getDailyNavThresholdGroups(after.short_window_sessions, after.long_window_sessions).flatMap(group =>
      group.fields.map(field => [field.key, `${group.title} — ${field.label}`] as const)
    )
  );
  DAILY_NAV_THRESHOLD_KEYS.forEach(key => {
    if (before[key] !== after[key]) {
      rows.push({
        key,
        label: thresholdLabels.get(key) ?? key,
        from: thresholdDisplay(before[key]),
        to: thresholdDisplay(after[key]),
      });
    }
  });

  DAILY_NAV_WINDOW_SESSIONS_FIELDS.forEach(field => {
    if (before[field.key] !== after[field.key]) {
      rows.push({
        key: field.key,
        label: field.label,
        from: String(before[field.key]),
        to: String(after[field.key]),
      });
    }
  });

  if (before.trading_calendar !== after.trading_calendar) {
    rows.push({
      key: 'trading_calendar',
      label: 'Trading Market',
      from: tradingMarketLabel(before.trading_calendar),
      to: tradingMarketLabel(after.trading_calendar),
    });
  }

  // Order-insensitive: reordering the same addresses notifies the same people.
  const beforeRecipients = Array.isArray(before.notification_recipients)
    ? [...(before.notification_recipients as string[])].sort()
    : [];
  const afterRecipients = Array.isArray(after.notification_recipients)
    ? [...(after.notification_recipients as string[])].sort()
    : [];
  if (beforeRecipients.join(' ') !== afterRecipients.join(' ')) {
    rows.push({
      key: 'notification_recipients',
      label: 'Daily Alert Notification Recipients',
      from: recipientsDisplay(beforeRecipients),
      to: recipientsDisplay(afterRecipients),
    });
  }

  return rows;
};

// What a company config save changed. A different shape from the firm diff: only
// thresholds can vary per company (windows and market are firm-only), they are NULLABLE
// -- null means "inherit" and reads as such rather than as a number -- and comps and the
// Alexandria link have no firm counterpart at all.
export const buildDailyNavCompanyConfigDiff = (
  before?: CompanyDailyNavConfigVersion | null,
  after?: CompanyDailyNavConfigVersion | null
): DailyNavConfigDiffRow[] => {
  if (!before || !after) {
    return [];
  }
  const rows: DailyNavConfigDiffRow[] = [];

  // Windows come from the anchoring FIRM version, since the company has none of its own.
  const labels = new Map(
    getDailyNavThresholdGroups(
      after.firm_config_version?.short_window_sessions,
      after.firm_config_version?.long_window_sessions
    ).flatMap(group => group.fields.map(field => [field.key, `${group.title} — ${field.label}`] as const))
  );
  // Null vs a value is itself the change, so compare before formatting: an override
  // being dropped back to inheritance is as real an edit as changing its number.
  DAILY_NAV_THRESHOLD_KEYS.forEach(key => {
    if ((before[key] ?? null) !== (after[key] ?? null)) {
      rows.push({
        key,
        label: labels.get(key) ?? key,
        from: before[key] == null ? INHERITED_LABEL : thresholdDisplay(before[key]),
        to: after[key] == null ? INHERITED_LABEL : thresholdDisplay(after[key]),
      });
    }
  });

  // Identity, not order: the watchlist is a set, and `order` only drives display.
  const compKeys = (version: CompanyDailyNavConfigVersion) =>
    (version.comps ?? []).map(comp => comp.ticker_symbol || comp.cap_iq_id).sort();
  const beforeComps = compKeys(before);
  const afterComps = compKeys(after);
  if (beforeComps.join(' ') !== afterComps.join(' ')) {
    rows.push({
      key: 'comps',
      label: 'Tracked comps',
      from: beforeComps.length ? beforeComps.join(', ') : 'None',
      to: afterComps.length ? afterComps.join(', ') : 'None',
    });
  }

  if ((before.alexandria_id ?? null) !== (after.alexandria_id ?? null)) {
    rows.push({
      key: 'alexandria_id',
      label: 'Company profile match',
      from: before.alexandria_id || 'Not matched',
      to: after.alexandria_id || 'Not matched',
    });
  }

  return rows;
};

// One threshold as it applies to a specific company: the value actually in force, the
// firm default behind it, and which of the two won.
export type DailyNavEffectiveRule = {
  key: DailyNavThresholdKey;
  group: string;
  label: string;
  value: string;
  firmValue: string;
  isOverride: boolean;
};

// EVERY threshold a row is judged by, not only overridden ones: someone checking why a
// cell went red needs the number either way. Windowed by the row's OWN config, so a
// historical row reads against its day's counts.
export const buildDailyNavEffectiveRules = (config?: DailyNavRecordConfig | null): DailyNavEffectiveRule[] => {
  if (!config) {
    return [];
  }
  const overrides = config.company_overrides ?? {};
  return getDailyNavThresholdGroups(config.short_window_sessions, config.long_window_sessions).flatMap(group =>
    group.fields.map(field => {
      // Presence, not value inequality -- see dailyNavRowHasOverrides.
      const isOverride = field.key in overrides;
      return {
        key: field.key,
        group: group.title,
        label: field.label,
        value: thresholdDisplay(isOverride ? overrides[field.key] : config.firm_thresholds?.[field.key]),
        firmValue: thresholdDisplay(config.firm_thresholds?.[field.key]),
        isOverride,
      };
    })
  );
};

// The single reason an Apply button is disabled, in precedence order. Shared so the
// firm-wide and company-scoped buttons can never explain themselves differently.
// Empty string when the button is enabled -- MUI renders no tooltip for an empty title.
export const dailyNavApplyDisabledReason = ({
  isDisabled,
  isDirty,
  hasNoOpenNavDay,
  hasNothingToApply,
  blockedDate,
}: {
  isDisabled: boolean;
  isDirty: boolean;
  hasNoOpenNavDay: boolean;
  // Open and applicable, but every record already sits on its company's open version.
  hasNothingToApply?: boolean;
  blockedDate: DailyNavConfigApplyDate | null;
}): string => {
  if (!isDisabled) {
    return '';
  }
  // Ahead of the API's reasons: unsaved edits aren't what would get applied.
  if (isDirty) {
    return DAILY_NAV_APPLY_UNSAVED_CHANGES_HINT;
  }
  const blockedMessage = dailyNavApplyBlockedMessage(blockedDate?.blocked_reason, blockedDate?.session_close);
  if (blockedMessage) {
    return blockedMessage;
  }
  // Outranks hasNothingToApply: with no dates the record count is zero too, and
  // "already uses these settings" would describe a day that does not exist.
  if (hasNoOpenNavDay) {
    return DAILY_NAV_APPLY_NO_OPEN_DAY_HINT;
  }
  return hasNothingToApply ? DAILY_NAV_APPLY_NOTHING_TO_APPLY_HINT : '';
};
